import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Space,
  Table,
  Tag,
  Typography,
} from '@surecompanies/core_components'
import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { downloadJSONasCSV } from 'helpers/download-file'
import {
  computeDuplicateBenefitIds,
  getRowErrors,
  isValidRow,
  rowToCsvRecord,
} from './bulkStatus.helpers'
import { useInvalidRowNavigation } from './useInvalidRowNavigation'
import {
  PREVIEW_TOTAL_SCROLL_WIDTH,
  usePreviewColumns,
} from './usePreviewColumns'
import { SubmitProgressModal } from './SubmitProgressModal'
import { useAllowedBulkStatuses } from './bulkStatus.constants'
import type { BatchProgress, ParsedRow, SubmitLog } from './bulkStatus.types'
import { PaymentAssignmentStatusId } from '../PaymentAssignment/Utils/types'
import { StatusUpdateFormKey } from '../PaymentAssignment/CaseManagement/statusUpdate.type'

const { Title } = Typography

interface PreviewStepProps {
  rows: ParsedRow[]
  period: Dayjs
  validCount: number
  invalidCount: number
  isSubmitting: boolean
  isCancelling: boolean
  batchProgress: BatchProgress
  rowErrors: Record<string, string>
  submitLog: SubmitLog | null
  onAddRow: () => void
  onDeleteRow: (key: string) => void
  onBenefitIdChange: (key: string, value: string) => void
  onStatusChange: (key: string, value: PaymentAssignmentStatusId) => void
  onNoteChange: (key: string, value: string) => void
  onFieldChange: (
    key: string,
    fieldKey: StatusUpdateFormKey,
    value: string
  ) => void
  onConfirm: () => void
  onReset: () => void
  onCancel: () => void
}

export function PreviewStep({
  rows,
  period,
  validCount,
  isSubmitting,
  isCancelling,
  batchProgress,
  rowErrors,
  submitLog,
  onAddRow,
  onDeleteRow,
  onBenefitIdChange,
  onStatusChange,
  onNoteChange,
  onFieldChange,
  onConfirm,
  onReset,
  onCancel,
}: PreviewStepProps) {
  const allowedStatuses = useAllowedBulkStatuses()
  // Warn the user before they navigate away / close the tab while a batch
  // submission is in progress, so they don't accidentally interrupt it.
  useEffect(() => {
    if (!isSubmitting) return
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isSubmitting])

  const duplicateBenefitIds = useMemo(
    () => computeDuplicateBenefitIds(rows),
    [rows]
  )

  const {
    tableContainerRef,
    focusedKey,
    handleInvalidTagClick,
    clearInvalidFocus,
  } = useInvalidRowNavigation(rows, duplicateBenefitIds, allowedStatuses)

  // Calculate actual invalid count (excluding duplicates)
  const actualInvalidCount = useMemo(() => {
    return rows.filter((r) => {
      if (duplicateBenefitIds.has(r.benefitId.trim().toLowerCase())) {
        return false // exclude duplicates
      }
      return !isValidRow(r, duplicateBenefitIds, allowedStatuses)
    }).length
  }, [rows, duplicateBenefitIds])

  const [focusedDuplicateIdx, setFocusedDuplicateIdx] = useState<number | null>(
    null
  )

  const duplicateKeys = useMemo(
    () =>
      rows
        .filter((r) =>
          duplicateBenefitIds.has(r.benefitId.trim().toLowerCase())
        )
        .map((r) => r.key),
    [rows, duplicateBenefitIds]
  )

  const jumpToDuplicate = useCallback(
    (idx: number) => {
      const key = duplicateKeys[idx]
      if (!key) return
      setFocusedDuplicateIdx(idx)
      setTimeout(() => {
        const container = tableContainerRef.current
        if (!container) return
        const tr = container.querySelector<HTMLElement>(
          `tr[data-row-key="${key}"]`
        )
        if (tr) tr.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 50)
    },
    [duplicateKeys, tableContainerRef]
  )

  const handleDuplicateTagClick = useCallback(() => {
    if (duplicateKeys.length === 0) return
    const nextIdx =
      focusedDuplicateIdx === null ||
      focusedDuplicateIdx >= duplicateKeys.length - 1
        ? 0
        : focusedDuplicateIdx + 1
    jumpToDuplicate(nextIdx)
  }, [duplicateKeys, focusedDuplicateIdx, jumpToDuplicate])

  // Wrapper handlers that clear the other focus type
  const handleInvalidTagClickWithClear = useCallback(() => {
    setFocusedDuplicateIdx(null)
    handleInvalidTagClick()
  }, [handleInvalidTagClick])

  const handleDuplicateTagClickWithClear = useCallback(() => {
    clearInvalidFocus()
    handleDuplicateTagClick()
  }, [clearInvalidFocus, handleDuplicateTagClick])

  const handleDownloadFailedAndInvalid = useCallback(() => {
    // Use row key as unique identifier to preserve all rows, including duplicates
    const recordByKey = new Map<string, Record<string, string>>()

    if (submitLog) {
      submitLog.failures.forEach((f) => {
        // Find all rows with this benefit ID (may be multiple if duplicates)
        rows
          .filter((r) => r.benefitId.trim() === f.benefitId.trim())
          .forEach((row) => {
            recordByKey.set(row.key, {
              ...rowToCsvRecord(row),
              error: f.message,
            })
          })
      })
    }

    rows
      .filter((r) => !isValidRow(r, duplicateBenefitIds, allowedStatuses))
      .forEach((r) => {
        // Only add if not already added as a failure (failure takes precedence)
        if (!recordByKey.has(r.key)) {
          recordByKey.set(r.key, {
            ...rowToCsvRecord(r),
            error: getRowErrors(r, duplicateBenefitIds, allowedStatuses).join(
              '; '
            ),
          })
        }
      })

    if (recordByKey.size === 0) return
    downloadJSONasCSV(
      Array.from(recordByKey.values()),
      'payment-assignment-bulk-status-failed-invalid.csv'
    )
  }, [rows, submitLog, duplicateBenefitIds])

  const columns = usePreviewColumns({
    isSubmitting,
    rowErrors,
    duplicateBenefitIds,
    onBenefitIdChange,
    onStatusChange,
    onNoteChange,
    onFieldChange,
    onDeleteRow,
  })

  return (
    <Card style={{ padding: '24px' }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            Preview &amp; Edit
          </Title>
          <Button onClick={onReset} disabled={isSubmitting}>
            Back to Upload
          </Button>
        </Space>

        <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
          <Space wrap>
            <Tag color="blue">Period: {period.format('MMMM YYYY')}</Tag>
            <Tag color="success">
              <CheckCircleOutlined /> {validCount} valid
            </Tag>
            {actualInvalidCount > 0 && (
              <Tag
                color="error"
                style={{ cursor: 'pointer' }}
                onClick={handleInvalidTagClickWithClear}
                title="Click to jump to first invalid row"
              >
                <CloseCircleOutlined /> {actualInvalidCount} invalid
              </Tag>
            )}
            {duplicateBenefitIds.size > 0 && (
              <Tag
                color="warning"
                style={{ cursor: 'pointer' }}
                onClick={handleDuplicateTagClickWithClear}
                title="Click to jump to first duplicate row"
              >
                {duplicateBenefitIds.size} duplicate
              </Tag>
            )}
          </Space>
          <Button
            icon={<PlusOutlined />}
            onClick={onAddRow}
            disabled={isSubmitting}
          >
            Add Row
          </Button>
        </Space>

        {validCount === 0 && actualInvalidCount > 0 && !submitLog && (
          <Alert
            type="error"
            showIcon
            message="No valid rows to submit. Please fix errors."
          />
        )}

        {submitLog && (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <div>
              <Typography.Title level={5} style={{ margin: 0 }}>
                Update partially completed
              </Typography.Title>
            </div>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {submitLog.successCount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                  <span>
                    {submitLog.successCount} benefit(s) update successfully and
                    removed from this list
                  </span>
                </div>
              )}
              {submitLog.failures.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CloseCircleOutlined
                    style={{ color: '#ff4d4f', fontSize: 16 }}
                  />
                  <span>
                    {submitLog.failures.length} item(s) failed. See the error
                    message under each benefit ID below.
                  </span>
                </div>
              )}
              {actualInvalidCount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CloseCircleOutlined
                    style={{ color: '#ff4d4f', fontSize: 16 }}
                  />
                  <span>
                    {actualInvalidCount} invalid row(s) were skipped and left in
                    the list for you to fix.
                  </span>
                </div>
              )}
              {duplicateBenefitIds.size > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CloseCircleOutlined
                    style={{ color: '#ff4d4f', fontSize: 16 }}
                  />
                  <span>
                    {duplicateBenefitIds.size} duplicate Benefit ID(s) were
                    skipped.
                  </span>
                </div>
              )}
            </Space>
            {(submitLog.failures.length > 0 ||
              actualInvalidCount > 0 ||
              duplicateBenefitIds.size > 0) && (
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={handleDownloadFailedAndInvalid}
              >
                Download Failed &amp; Invalid (CSV)
              </Button>
            )}
          </Space>
        )}

        <div
          ref={tableContainerRef}
          style={{
            border: '1px solid #e8e8e8',
            borderRadius: 6,
            overflow: 'auto',
          }}
        >
          <Table
            dataSource={rows}
            columns={columns}
            rowKey="key"
            size="small"
            scroll={{ x: PREVIEW_TOTAL_SCROLL_WIDTH, y: 460 }}
            pagination={false}
            rowClassName={(row) => {
              const focusedDuplicateKey =
                focusedDuplicateIdx !== null
                  ? duplicateKeys[focusedDuplicateIdx]
                  : null
              if (row.key === focusedKey || row.key === focusedDuplicateKey)
                return 'ant-table-row-focused'
              if (rowErrors[row.key]) return 'ant-table-row-error'
              if (!isValidRow(row, duplicateBenefitIds, allowedStatuses))
                return 'ant-table-row-error'
              return ''
            }}
          />
        </div>

        <style>{`
          .ant-table-row-error td { background-color: #fff2f0 !important; }
          .ant-table-row-focused td {
            background-color: #fffbe6 !important;
          }
          .ant-table-cell { vertical-align: top !important; padding: 4px 6px !important; }
          .ant-table-thead .ant-table-cell {
            background: #fafafa !important;
            font-size: 11px !important;
            padding: 6px 6px !important;
            white-space: nowrap;
          }
        `}</style>

        {isSubmitting && batchProgress.total > 0 && (
          <SubmitProgressModal
            open={isSubmitting}
            isCancelling={isCancelling}
            batchProgress={batchProgress}
            onCancel={onCancel}
          />
        )}

        <Space>
          <Button
            type="primary"
            disabled={validCount === 0 || isSubmitting}
            onClick={onConfirm}
          >
            {submitLog ? 'Resubmit' : 'Confirm & Submit'} {validCount} Update(s)
          </Button>
          <Button onClick={onReset} disabled={isSubmitting}>
            Cancel
          </Button>
        </Space>
      </Space>
    </Card>
  )
}
